import { apiService } from '@/services/apiService'
import { AuthService } from '@/services/authService'
import { deviceTokenService } from '@/services/deviceTokenService'
import { Auth } from '@/types/common/httpRequest'
import { serializeError } from '@/utilities/serializeError'

export { serializeError }

/**
 * Lightweight diagnostic helpers used to dump terminal state into the console
 * (and therefore into the Sentry replay) when the kiosk lands on an
 * error/recovery screen such as /register or /connectivity-issues.
 *
 * Tokens are deliberately not logged in full - only presence + length + a
 * trailing fingerprint - so we can correlate "is this the same token?"
 * without exposing the raw bearer credential.
 */

interface TokenSummary {
    present: boolean
    length: number
    suffix: string
}

const summarizeToken = (raw: string | null): TokenSummary => {
    if (!raw) return { present: false, length: 0, suffix: '' }
    const stripped = raw.replace(/^Bearer\s+/, '')
    return {
        present: true,
        length: stripped.length,
        suffix: stripped.slice(-4)
    }
}

export interface DiagnosticSnapshot {
    timestamp: string
    pathname: string
    apiMode: string
    apiBaseUrl: string
    deviceToken: TokenSummary
    cashierToken: TokenSummary
    deviceTokenServiceInitialized: boolean
    deviceHasBeenRegistered: boolean
    localApiNetworkError: boolean
    localApiRespondedEmpty: boolean
    shouldNavigateToRegister: boolean
    shouldNavigateToConnectivityIssues: boolean
    navigatorOnLine: boolean
    userAgent: string
}

/**
 * Capture a snapshot of everything we need to understand why the kiosk has
 * landed on a recovery screen: which tokens we have, whether the local
 * device API is healthy, and which API mode/base-URL we're talking to.
 */
export const captureDiagnosticSnapshot = (): DiagnosticSnapshot => {
    return {
        timestamp: new Date().toISOString(),
        pathname:
            typeof window !== 'undefined'
                ? window.location.pathname
                : '(no window)',
        apiMode: apiService.getMode(),
        apiBaseUrl: apiService.getBaseUrl(),
        deviceToken: summarizeToken(AuthService.GetToken(Auth.POS)),
        cashierToken: summarizeToken(AuthService.GetToken(Auth.Cashier)),
        deviceTokenServiceInitialized: deviceTokenService.isInitialized(),
        deviceHasBeenRegistered: deviceTokenService.hasBeenRegistered(),
        localApiNetworkError: deviceTokenService.hasLocalApiNetworkError(),
        localApiRespondedEmpty: deviceTokenService.hasLocalApiRespondedEmpty(),
        shouldNavigateToRegister: deviceTokenService.shouldNavigateToRegister(),
        shouldNavigateToConnectivityIssues:
            deviceTokenService.shouldNavigateToConnectivityIssues(),
        navigatorOnLine:
            typeof navigator !== 'undefined' ? navigator.onLine : false,
        userAgent:
            typeof navigator !== 'undefined'
                ? navigator.userAgent
                : '(no navigator)'
    }
}
